import axios from "axios";
import Cookies from "js-cookie";
import { AUTH_LOGOUT, AUTH_REFRESH, baseURL } from "../config/Endpoints";
import {
  ACCESS_TOKEN_COOKIE_EXPIRY_DAYS,
  REFRESH_TOKEN_COOKIE_EXPIRY_DAYS,
} from "../config/auth";

// The app has both Axios and fetch callers. Install once at startup so both
// paths use the same token rotation and retry behavior.
const nativeFetch = window.fetch.bind(window);
let refreshInFlight = null;
let installed = false;
let sessionVersion = 0;

const cookieOptions = {
  sameSite: "Lax",
  secure: window.location.protocol === "https:",
};

export function saveAuthTokens({ accessToken, refreshToken }) {
  if (!accessToken || !refreshToken) {
    throw new Error("Sign-in response is missing authentication tokens");
  }
  sessionVersion += 1;
  Cookies.set("accessToken", accessToken, {
    ...cookieOptions,
    expires: ACCESS_TOKEN_COOKIE_EXPIRY_DAYS,
  });
  Cookies.set("refreshToken", refreshToken, {
    ...cookieOptions,
    expires: REFRESH_TOKEN_COOKIE_EXPIRY_DAYS,
  });
}

export function clearAuthTokens() {
  sessionVersion += 1;
  Cookies.remove("accessToken");
  Cookies.remove("refreshToken");
}

function endExpiredSession() {
  clearAuthTokens();
  localStorage.removeItem("userData");
  if (window.location.pathname.startsWith("/dashboard")) {
    window.location.replace("/");
  }
}

function isApiUrl(url) {
  if (!baseURL) return false;
  try {
    const api = new URL(baseURL, window.location.origin);
    const target = new URL(url, window.location.origin);
    const prefix = api.pathname.replace(/\/$/, "");
    return target.origin === api.origin &&
      (target.pathname === prefix || target.pathname.startsWith(`${prefix}/`));
  } catch {
    return false;
  }
}

function isAuthUrl(url) {
  const pathname = new URL(url, window.location.origin).pathname;
  return /\/api\/auth\/(signin|refresh|logout)$/.test(pathname);
}

export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  const versionAtStart = sessionVersion;

  refreshInFlight = (async () => {
    const startingToken = Cookies.get("refreshToken");
    if (!startingToken) throw new Error("No refresh token available");

    const rotate = async () => {
      // Another tab may have rotated this user's single stored token while we waited.
      if (Cookies.get("refreshToken") !== startingToken && Cookies.get("accessToken")) {
        return Cookies.get("accessToken");
      }
      const refreshToken = Cookies.get("refreshToken");
      if (!refreshToken) throw new Error("No refresh token available");
      const response = await nativeFetch(AUTH_REFRESH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) throw new Error("Session expired");

      const tokens = await response.json();
      if (!tokens.accessToken || !tokens.refreshToken) {
        throw new Error("Invalid refresh response");
      }
      if (sessionVersion !== versionAtStart) {
        throw new Error("Session changed during refresh");
      }
      saveAuthTokens(tokens);
      return tokens.accessToken;
    };

    return navigator.locks?.request
      ? navigator.locks.request("hrmis-refresh-token", rotate)
      : rotate();
  })();

  try {
    return await refreshInFlight;
  } catch (error) {
    if (sessionVersion === versionAtStart) endExpiredSession();
    throw error;
  } finally {
    refreshInFlight = null;
  }
}

async function authenticatedFetch(input, init) {
  const request = new Request(input, init);
  if (!isApiUrl(request.url) || isAuthUrl(request.url)) {
    return nativeFetch(input, init);
  }

  const authorization = request.headers.get("Authorization");
  if (authorization && !/^Bearer\s/i.test(authorization)) {
    return nativeFetch(input, init);
  }
  const hadAuthorization = Boolean(authorization);
  const refreshToken = Cookies.get("refreshToken");
  if (!hadAuthorization && !refreshToken) return nativeFetch(input, init);

  let accessToken = Cookies.get("accessToken");
  if (!accessToken && refreshToken) accessToken = await refreshAccessToken();

  const send = (token) => {
    const headers = new Headers(request.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return nativeFetch(new Request(request.clone(), { headers }));
  };

  const response = await send(accessToken);
  if (response.status !== 401) return response;
  if (!Cookies.get("refreshToken")) {
    endExpiredSession();
    return response;
  }

  // A different request may already have rotated the token while this 401 was in flight.
  const currentAccessToken = Cookies.get("accessToken");
  const newAccessToken = currentAccessToken && currentAccessToken !== accessToken
    ? currentAccessToken
    : await refreshAccessToken();
  const retryResponse = await send(newAccessToken);
  if (retryResponse.status === 401 && Cookies.get("accessToken") === newAccessToken) {
    endExpiredSession();
  }
  return retryResponse;
}

export function installAuthInterceptors() {
  if (installed) return;
  installed = true;
  window.fetch = authenticatedFetch;

  axios.interceptors.request.use(async (config) => {
    if (!isApiUrl(config.url) || isAuthUrl(config.url)) return config;
    const authorization = config.headers?.get?.("Authorization") ||
      config.headers?.Authorization;
    if (authorization && !/^Bearer\s/i.test(authorization)) return config;
    const hasAuthorization = Boolean(authorization);
    const refreshToken = Cookies.get("refreshToken");
    if (!hasAuthorization && !refreshToken) return config;

    let accessToken = Cookies.get("accessToken");
    if (!accessToken && refreshToken) accessToken = await refreshAccessToken();
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  });

  axios.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (error.response?.status !== 401 || !config ||
        !isApiUrl(config.url) || isAuthUrl(config.url)) {
      return Promise.reject(error);
    }
    const authorization = config.headers?.get?.("Authorization") ||
      config.headers?.Authorization;
    if (authorization && !/^Bearer\s/i.test(authorization)) {
      return Promise.reject(error);
    }
    const failedAccessToken = typeof authorization === "string"
      ? authorization.match(/^Bearer\s+(.+)$/i)?.[1]
      : undefined;
    if (config._authRetried) {
      if (Cookies.get("accessToken") === failedAccessToken) endExpiredSession();
      return Promise.reject(error);
    }
    if (!Cookies.get("refreshToken")) {
      endExpiredSession();
      return Promise.reject(error);
    }
    config._authRetried = true;
    const currentAccessToken = Cookies.get("accessToken");
    const accessToken = currentAccessToken && failedAccessToken && currentAccessToken !== failedAccessToken
      ? currentAccessToken
      : await refreshAccessToken();
    config.headers.Authorization = `Bearer ${accessToken}`;
    return axios(config);
  });
}

export async function logoutSession() {
  const refreshToken = Cookies.get("refreshToken");
  clearAuthTokens();
  localStorage.clear();
  const abortController = new AbortController();
  const timeout = window.setTimeout(() => abortController.abort(), 3000);
  try {
    if (refreshToken) {
      await nativeFetch(AUTH_LOGOUT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
        signal: abortController.signal,
      });
    }
  } catch {
    // Local logout still succeeds if the server cannot be reached.
  } finally {
    window.clearTimeout(timeout);
    window.location.replace("/");
  }
}
